/**
 * API client for the KPI Tracker backend.
 * Token is injected by the caller (useApi hook) - not stored globally.
 */

import type {
  AuditLogPage,
  AuditLogQuery,
  BoardDateRules,
  BoardDateRulesUpdate,
  DdiDraftEvent,
  DdiDraftResult,
  MeResponse,
  MonthReview,
  ReportingWindowUpdate,
  RoleGroupMap,
  RoleGroupMapping,
  SurveyAnswer,
  SurveyForm,
  SurveyRecipient,
  SurveyDesign,
  SurveyRound,
  SurveySendResult,
  SurveyType,
  SurveyTypeResults,
  SurveyTypeSummary,
} from '../types'

const API = import.meta.env.VITE_PUBLIC_API_URL || '/api'

function createApi(getToken: () => Promise<string | null>) {
  const headers = async (extra?: Record<string, string>) => {
    const token = await getToken()
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...extra,
    }
  }

  const send = async (path: string, method: string, body?: unknown) => {
    const h = await headers(body ? { 'Content-Type': 'application/json' } : {})
    const resp = await fetch(`${API}${path}`, {
      method,
      headers: h,
      ...(body != null ? { body: JSON.stringify(body) } : {}),
    })
    if (resp.status === 204) return null
    const data = await resp.json().catch(() => null)
    if (!resp.ok) {
      // FastAPI errors come back as { detail: "..." }; surface that as a thrown Error
      // so callers can show a real message instead of silently treating 4xx as success.
      const message = (data && (data.detail || data.error)) || `Request failed (${resp.status})`
      const err = new Error(typeof message === 'string' ? message : JSON.stringify(message))
      ;(err as any).status = resp.status
      ;(err as any).data = data
      throw err
    }
    return data
  }

  return {
    // Projects
    getProjects: () => send('/projects', 'GET'),
    // 404 is a legitimate "not found" state, not a thrown error - the
    // ProjectDetail page renders a friendly empty state for null.
    getProject: (id: string) =>
      send(`/projects/${id}`, 'GET').catch((err: any) => {
        if (err?.status === 404) return null
        throw err
      }),
    createProject: (project: unknown) => send('/projects', 'POST', project),
    updateProject: (id: string, updates: unknown) => send(`/projects/${id}`, 'PATCH', updates),
    deleteProject: (id: string) => send(`/projects/${id}`, 'DELETE'),

    // Survey responses
    getSurveyResponses: (projectId?: string) =>
      send(projectId ? `/survey-responses?projectId=${projectId}` : '/survey-responses', 'GET'),

    // Public survey endpoints - no auth header needed, but harmless if one
    // is attached. The respondent is identified entirely by the
    // (projectToken, recipientToken) pair that comes in on the URL.
    getSurveyLookup: (projectToken: string, recipientToken: string) =>
      send(
        `/survey/lookup/${encodeURIComponent(projectToken)}/${encodeURIComponent(recipientToken)}`,
        'GET',
      ),

    checkSurveySubmitted: (projectToken: string, recipientToken: string) =>
      send(
        `/survey/check/${encodeURIComponent(projectToken)}/${encodeURIComponent(recipientToken)}`,
        'GET',
      ),

    submitSurvey: (body: {
      projectToken: string
      recipientToken: string
      techSatisfaction: number
      peopleSatisfaction: number
      adoption?: number
      valueRealised?: number
      additionalComments?: string
    }) => send('/survey/submit', 'POST', body),

    // Field mappings
    getFieldMappings: () => send('/field-mappings', 'GET'),
    saveFieldMapping: (mapping: unknown) => send('/field-mappings', 'POST', mapping),
    updateFieldMapping: (id: string, updates: unknown) => send(`/field-mappings/${id}`, 'PATCH', updates),

    // Settings
    getSettings: () => send('/settings', 'GET'),
    updateSettings: (settings: unknown) => send('/settings', 'PUT', settings),

    // Send survey emails via Power Automate
    sendSurvey: (data: { projectId: string; projectName: string; surveyToken: string; stakeholders: string[]; origin: string }) =>
      send('/send-survey', 'POST', data),

    // Products
    getProducts: () => send('/products', 'GET'),
    getProduct: (id: string) => send(`/products/${id}`, 'GET'),
    createProduct: (product: unknown) => send('/products', 'POST', product),
    updateProduct: (id: string, updates: unknown) => send(`/products/${id}`, 'PATCH', updates),
    deleteProduct: (id: string) => send(`/products/${id}`, 'DELETE'),
    refreshForecastYears: (id: string) => send(`/projects/${id}/ledger/refresh-forecast`, 'POST'),

    // The caller's identity and server-resolved roles. Authorization is read
    // from here rather than derived from the ID token's group claims.
    getMe: (): Promise<MeResponse> => send('/me', 'GET'),

    // User directory - self-seeded by `current_user` on every authenticated request.
    // Returns the canonical record (oid is stable; name/email refresh on rename).
    // `includeRoles` is platform admin only, so the picker asks without it.
    getUsers: (opts?: { includeRoles?: boolean }) =>
      send(`/users${opts?.includeRoles ? '?includeRoles=true' : ''}`, 'GET'),

    // Central audit log, newest first. Platform admin only.
    getAuditLog: (params?: AuditLogQuery): Promise<AuditLogPage> => {
      const query = new URLSearchParams()
      if (params?.limit != null) query.set('limit', String(params.limit))
      if (params?.offset != null) query.set('offset', String(params.offset))
      if (params?.entityType) query.set('entityType', params.entityType)
      if (params?.actorOid) query.set('actorOid', params.actorOid)
      if (params?.since) query.set('since', params.since)
      return send(`/audit${query.toString() ? `?${query.toString()}` : ''}`, 'GET')
    },

    // Admin
    getAsanaSyncRuns: () => send('/admin/sync/asana/runs', 'GET'),
    getNextAsanaSyncRun: () => send('/admin/sync/asana/next', 'GET'),
    // Next scheduled owner-phase and group-phase reminder send dates
    getNextReminderSends: () => send('/admin/reminders/next', 'GET'),
    // Sends the monthly reminder email to the CALLER for content review
    sendTestReminder: () => send('/admin/reminders/test', 'POST'),
    // Fires a real phase send NOW to its actual recipients (owners / group)
    runOwnerReminders: () => send('/admin/reminders/run/owners', 'POST'),
    runGroupReminder: () => send('/admin/reminders/run/group', 'POST'),

    // BAU
    getBauSnapshot: () => send('/bau/snapshot', 'GET'),
    triggerBauSync: () => send('/bau/snapshot/sync', 'POST'),

    // Jira (Commercial AEM BAU)
    getJiraSnapshot: () => send('/jira/snapshot', 'GET'),
    triggerJiraSync: () => send('/jira/snapshot/sync', 'POST'),

    // Month-end review sign-offs (one doc per YYYY-MM month)
    getMonthReview: (month: string) => send(`/month-reviews/${month}`, 'GET'),
    setMonthReview: (month: string, projectId: string) => send(`/month-reviews/${month}/${projectId}`, 'PUT'),
    clearMonthReview: (month: string, projectId: string) => send(`/month-reviews/${month}/${projectId}`, 'DELETE'),
    // Reporting windows live on the month-review doc; writes need a status
    // reporting admin on the backend.
    setReportingWindow: (month: string, body: ReportingWindowUpdate) =>
      send(`/month-reviews/${month}/window`, 'PUT', body),
    // Readable by anyone signed in; writes need a status reporting admin.
    getBoardDateRules: (): Promise<BoardDateRules> => send('/board-date-rules', 'GET'),
    setBoardDateRules: (body: BoardDateRulesUpdate): Promise<BoardDateRules> =>
      send('/board-date-rules', 'PUT', body),
    // Which Entra group means which role. Platform admin only, both ways:
    // membership is held in Entra, and this says what it grants.
    getRoleGroups: (): Promise<RoleGroupMap> => send('/role-groups', 'GET'),
    setRoleGroups: (mappings: RoleGroupMapping[]): Promise<RoleGroupMap> =>
      send('/role-groups', 'PUT', { mappings }),
    setReportingExtension: (month: string, projectId: string, body: { closesAt: string; reason: string }) =>
      send(`/month-reviews/${month}/extensions/${projectId}`, 'PUT', body),
    clearReportingExtension: (month: string, projectId: string) =>
      send(`/month-reviews/${month}/extensions/${projectId}`, 'DELETE'),
    // A month's DDI texts close and reopen by hand once its window is saved.
    // Status reporting admin only; both are no-ops when already done and
    // return the month review.
    closeDdiTexts: (month: string): Promise<MonthReview> =>
      send(`/month-reviews/${month}/ddi-texts/close`, 'POST'),
    reopenDdiTexts: (month: string): Promise<MonthReview> =>
      send(`/month-reviews/${month}/ddi-texts/reopen`, 'POST'),
    // A fresh AI draft of one row's DDI texts, not saved: the page drops it
    // into the editor and autosave keeps it.
    redraftProjectReportDdi: (month: string, projectId: string): Promise<{ ddiSummary: string; ddiNextSteps: string; issues: string[] }> =>
      send(`/project-reports/${month}/${projectId}/ddi-draft`, 'POST'),
    // Drafts the whole DDI slide with AI, once a month: fills and saves every
    // row with a filed report and no text yet, marked as an AI draft.
    // Progress streams back one JSON line per step, passed to `onProgress`.
    // `signal` stops listening; the server finishes the rows it has started
    // and saves them, so the page refetches after a stop.
    draftDdiSlide: async (month: string, onProgress: (e: DdiDraftEvent) => void, signal?: AbortSignal): Promise<DdiDraftResult> => {
      const resp = await fetch(`${API}/month-reviews/${month}/ddi-draft`, { method: 'POST', headers: await headers(), signal })
      if (!resp.ok || !resp.body) {
        const data = await resp.json().catch(() => null)
        throw new Error((data && typeof data.detail === 'string' && data.detail) || `Request failed (${resp.status})`)
      }
      const reader = resp.body.getReader()
      const decoder = new TextDecoder()
      let buffered = ''
      for (;;) {
        const { value, done } = await reader.read()
        buffered += decoder.decode(value, { stream: !done })
        const lines = buffered.split('\n')
        buffered = done ? '' : lines.pop() ?? ''
        for (const line of lines.filter(l => l.trim())) {
          const event = JSON.parse(line)
          if (event.error) throw new Error(event.error)
          if (event.result) return event.result as DdiDraftResult
          onProgress(event as DdiDraftEvent)
        }
        if (done) throw new Error('Drafting stopped before it finished. Refresh to see what was saved.')
      }
    },

    // Monthly project status reports (one doc per project per YYYY-MM period).
    // getProjectReport resolves to null when nothing has been filed yet - that's
    // the normal starting state each month, not an error, and the editor
    // prefills from the project when it sees null.
    getProjectReports: (month: string) => send(`/project-reports/${month}`, 'GET'),
    getProjectReport: (month: string, projectId: string) =>
      send(`/project-reports/${month}/${projectId}`, 'GET'),
    // Content only: the report keeps its stored state (a new one starts as a
    // draft), so an editor left open can't undo a sign-off made elsewhere.
    saveProjectReport: (month: string, projectId: string, report: unknown) =>
      send(`/project-reports/${month}/${projectId}`, 'PUT', report),
    // The only way a report's state moves: the month-end sign-off, and the
    // editor's Submit / Mark reviewed. Never resends the report body, so the
    // month-end review can't write back a stale copy of content the PM changed
    // in the meantime. With expectedState it only moves a report still in that
    // state, and otherwise returns the report as it stands.
    setProjectReportState: (month: string, projectId: string, state: string, expectedState?: string) =>
      send(`/project-reports/${month}/${projectId}/state`, 'PATCH', { state, expectedState }),
    // DDI texts only. Like the state PATCH, it never carries the report's
    // content, so the DDI page cannot write back a stale copy of it.
    setProjectReportDdi: (
      month: string,
      projectId: string,
      texts: { ddiSummary: string; ddiNextSteps: string },
    ) => send(`/project-reports/${month}/${projectId}/ddi`, 'PATCH', texts),
    deleteProjectReport: (month: string, projectId: string) =>
      send(`/project-reports/${month}/${projectId}`, 'DELETE'),

    // Surveys sent by hand (the leadership survey). Setting them up and
    // sending them is platform admin only; the results need the Survey
    // results role.
    getSurveyTypes: (): Promise<SurveyType[]> => send('/survey-types', 'GET'),
    createSurveyType: (body: SurveyDesign & { name: string; description: string }): Promise<SurveyType> =>
      send('/survey-types', 'POST', body),
    updateSurveyType: (id: string, body: SurveyDesign & { name: string; description: string }): Promise<SurveyType> =>
      send(`/survey-types/${id}`, 'PUT', body),
    deleteSurveyType: (id: string) => send(`/survey-types/${id}`, 'DELETE'),
    getSurveyRounds: (typeId?: string): Promise<SurveyRound[]> =>
      send(typeId ? `/survey-rounds?typeId=${encodeURIComponent(typeId)}` : '/survey-rounds', 'GET'),
    createSurveyRound: (body: { typeId: string; name: string; recipientsFrom?: string }): Promise<SurveyRound> =>
      send('/survey-rounds', 'POST', body),
    updateSurveyRound: (id: string, body: SurveyDesign & { name: string; recipients: SurveyRecipient[] }): Promise<SurveyRound> =>
      send(`/survey-rounds/${id}`, 'PUT', body),
    deleteSurveyRound: (id: string) => send(`/survey-rounds/${id}`, 'DELETE'),
    // Both email through the Power Automate flow; `origin` is where the links open.
    sendSurveyRound: (id: string, origin: string): Promise<SurveySendResult> =>
      send(`/survey-rounds/${id}/send`, 'POST', { origin }),
    remindSurveyRound: (id: string, origin: string): Promise<SurveySendResult> =>
      send(`/survey-rounds/${id}/remind`, 'POST', { origin }),
    closeSurveyRound: (id: string): Promise<SurveyRound> => send(`/survey-rounds/${id}/close`, 'POST'),
    reopenSurveyRound: (id: string): Promise<SurveyRound> => send(`/survey-rounds/${id}/reopen`, 'POST'),
    getSurveyResultTypes: (): Promise<SurveyTypeSummary[]> => send('/survey-results', 'GET'),
    getSurveyResults: (typeId: string): Promise<SurveyTypeResults> =>
      send(`/survey-results/${encodeURIComponent(typeId)}`, 'GET'),
    // Public: the link's token is the credential, so it goes in the body,
    // never the URL (request logs record URLs). 404 = unknown round or link.
    getSurveyForm: (roundId: string, token: string): Promise<SurveyForm | null> =>
      send(`/feedback/${encodeURIComponent(roundId)}/open`, 'POST', { token }).catch((err: any) => {
        if (err?.status === 404) return null
        throw err
      }),
    submitSurveyAnswers: (roundId: string, token: string, answers: Record<string, SurveyAnswer>, area?: string) =>
      send(`/feedback/${encodeURIComponent(roundId)}`, 'POST', { token, answers, area: area ?? '' }),

    // Saved views
    getViews: (page: string) => send(`/views?page=${page}`, 'GET'),
    createView: (body: { name: string; page: string; filters: unknown }) => send('/views', 'POST', body),
    updateView: (id: string, body: { name?: string; filters?: unknown }) => send(`/views/${id}`, 'PUT', body),
    deleteView: (id: string) => send(`/views/${id}`, 'DELETE'),
  }
}

export type Api = ReturnType<typeof createApi>
export { createApi }
